import React, { useState, useEffect, useCallback } from 'react';
import { X, Settings, Bell, LayoutDashboard, Target, Building, User, AlertCircle } from 'lucide-react';
import { goalService } from '../../services/goalService';
import { useAuth } from '../../contexts/AuthContext';
import { hasAnyRole } from '../../utils/permissions';

/**
 * GoalSettingsPanel - Slide-out panel for goal settings
 *
 * Props:
 * - isOpen: boolean
 * - onClose: function()
 */
const GoalSettingsPanel = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const isAdmin = hasAnyRole(user, ['Super Admin', 'Admin']);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('user');

  const [userSettings, setUserSettings] = useState({
    goals_enabled: true,
    show_on_dashboard: true,
    notification_enabled: true,
  });

  const [firmSettings, setFirmSettings] = useState({
    goals_enabled: true,
    show_on_dashboard: true,
    notification_enabled: true,
  });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const userResponse = await goalService.getUserSettings();
      setUserSettings(userResponse.data);

      if (isAdmin) {
        const firmResponse = await goalService.getFirmSettings();
        setFirmSettings(firmResponse.data);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen, loadSettings]);

  const handleUserSettingChange = async (key, value) => {
    const newSettings = { ...userSettings, [key]: value };
    setUserSettings(newSettings);
    setSaving(true);

    try {
      await goalService.updateUserSettings({ [key]: value });
    } catch (err) {
      console.error('Error saving user settings:', err);
      // Revert on error
      setUserSettings(userSettings);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFirmSettingChange = async (key, value) => {
    const newSettings = { ...firmSettings, [key]: value };
    setFirmSettings(newSettings);
    setSaving(true);

    try {
      await goalService.updateFirmSettings({ [key]: value });
    } catch (err) {
      console.error('Error saving firm settings:', err);
      // Revert on error
      setFirmSettings(firmSettings);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-yellow-50 to-orange-50 p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-xl">
                <Settings className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Goal Settings</h2>
                <p className="text-sm text-gray-500">Customize your goals experience</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 100px)' }}>
          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Tabs - Show only if admin */}
          {isAdmin && (
            <div className="flex space-x-2 mb-6">
              <button
                onClick={() => setActiveTab('user')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'user'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <User className="h-4 w-4" />
                <span>Personal</span>
              </button>
              <button
                onClick={() => setActiveTab('firm')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'firm'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Building className="h-4 w-4" />
                <span>Firm</span>
              </button>
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-10 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {/* User Settings */}
              {activeTab === 'user' && (
                <>
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <h3 className="font-semibold text-gray-900 flex items-center">
                      <User className="h-5 w-5 text-gray-600 mr-2" />
                      Personal Settings
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      These settings only affect your personal view.
                    </p>
                  </div>

                  <SettingToggle
                    icon={Target}
                    label="Goals Feature"
                    description="Enable goal tracking for your account"
                    enabled={userSettings.goals_enabled}
                    onChange={(value) => handleUserSettingChange('goals_enabled', value)}
                    saving={saving}
                  />

                  <SettingToggle
                    icon={LayoutDashboard}
                    label="Show on Dashboard"
                    description="Display goals widget on your dashboard"
                    enabled={userSettings.show_on_dashboard}
                    onChange={(value) => handleUserSettingChange('show_on_dashboard', value)}
                    saving={saving}
                    disabled={!userSettings.goals_enabled}
                  />

                  <SettingToggle
                    icon={Bell}
                    label="Notifications"
                    description="Receive notifications about goal progress"
                    enabled={userSettings.notification_enabled}
                    onChange={(value) => handleUserSettingChange('notification_enabled', value)}
                    saving={saving}
                    disabled={!userSettings.goals_enabled}
                  />
                </>
              )}

              {/* Firm Settings (Admin Only) */}
              {activeTab === 'firm' && isAdmin && (
                <>
                  <div className="bg-blue-50 rounded-xl p-4 mb-4">
                    <h3 className="font-semibold text-gray-900 flex items-center">
                      <Building className="h-5 w-5 text-blue-600 mr-2" />
                      Firm-Wide Settings
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      These settings affect all users in your firm.
                    </p>
                  </div>

                  <SettingToggle
                    icon={Target}
                    label="Firm Goals Feature"
                    description="Enable firm-level goal tracking"
                    enabled={firmSettings.goals_enabled}
                    onChange={(value) => handleFirmSettingChange('goals_enabled', value)}
                    saving={saving}
                  />

                  <SettingToggle
                    icon={LayoutDashboard}
                    label="Show Firm Goals on Dashboard"
                    description="Display firm goals on team dashboards"
                    enabled={firmSettings.show_on_dashboard}
                    onChange={(value) => handleFirmSettingChange('show_on_dashboard', value)}
                    saving={saving}
                    disabled={!firmSettings.goals_enabled}
                  />

                  <SettingToggle
                    icon={Bell}
                    label="Team Notifications"
                    description="Send notifications to team about firm goals"
                    enabled={firmSettings.notification_enabled}
                    onChange={(value) => handleFirmSettingChange('notification_enabled', value)}
                    saving={saving}
                    disabled={!firmSettings.goals_enabled}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Setting Toggle Component
const SettingToggle = ({
  icon: Icon,
  label,
  description,
  enabled,
  onChange,
  saving,
  disabled = false,
}) => {
  return (
    <div className={`flex items-start justify-between p-4 rounded-xl border ${
      disabled ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-start space-x-3">
        <div className={`p-2 rounded-lg ${
          disabled ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 text-gray-600'
        }`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className={`font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>
            {label}
          </p>
          <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>
            {description}
          </p>
        </div>
      </div>

      <button
        onClick={() => !disabled && !saving && onChange(!enabled)}
        disabled={disabled || saving}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        } ${enabled ? 'bg-yellow-500' : 'bg-gray-300'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
};

export default GoalSettingsPanel;
